import { useState } from 'react';
import { FieldValues, UseFormProps } from 'react-hook-form';
import { anonymizeUrl } from '@mews-commander/utils';
import { FilterSidebar } from '@mews/b2b-ui';
import { createZodFormResolver, lt, useFormControls, useGainsightService, useSafeUrlState } from '@mews/framework';
import { Button, Container, getBreakpointRange, isAtMostTablet, Stack } from '@optimus-web/core';
import { FilterSidebarLayoutContentProps } from './types';

const contentSpacing = '300';
const formId = 'filterSidebarForm';

/**
 * Layout content for creating views with filter sidebar
 * @description The layout's filter values are driven by supplied Zod schema which are persisted to URL on submit:
 * @param props.filterSchema - schema for the filter form
 * @param props.defaultFilterValues - default values for the filter form
 * @param props.queryParamsSchema - schema for the query parameters (may or may not include part(s) of the filter schema), can be omitted if it is the same as filter schema
 * @param props.defaultQueryParams - default (constant) query parameter values
 * @description Other props:
 * @param props.children - main content rendered to the left of the filter sidebar, can also be passed as render props function whose argument are the default and submitted filter values
 * @param props.formProps - props object passed to useForm hook, resolver is omitted since it will be created with the supplied Zod schema
 * @param props.filterSidebar.title - title component for the filter sidebar
 * @param props.filterSidebar.fields - react-hook-form fields for the filter form
 * @param props.filterSidebar.actions - additional secondary buttons rendered to the left of the submit button
 * @param props.submitButtonLabel - label for submit button, by default it is localized value of "ViewReport"
 * @param props.onFilterSubmit - callback function called with submitted filter values on successful filter form submission
 * @param props.loading - boolean flag to show loading spinner on submit button
 * @param props.withClearFilters - boolean flag to show Clear Filters button to reset form into default state
 * @param props.toggleAfterSubmit - bollean flag to determin if filter sidebar is closed after submit
 */
export const FilterSidebarLayoutContent = <F extends FieldValues, Q = {}>({
    children,
    formProps = {},
    filterSchema,
    defaultFilterValues,
    queryParamsSchema,
    defaultQueryParams,
    filterSidebar,
    onFilterSubmit,
    submitButtonLabel,
    loading,
    showSidebar,
    toggleSidebar,
    withClearFilters,
    toggleAfterSubmit,
}: FilterSidebarLayoutContentProps<F, Q>) => {
    const gainsightService = useGainsightService();
    const [urlState, setUrlState] = useSafeUrlState<Q | F>(queryParamsSchema || filterSchema);

    const defaultValuesParseResult = filterSchema.safeParse({ ...defaultFilterValues, ...urlState });
    const [filterValues, setFilterValues] = useState<F>(
        defaultValuesParseResult.success ? defaultValuesParseResult.data : defaultFilterValues
    );

    const isSmallDevice = isAtMostTablet(getBreakpointRange());

    const handleSubmit = (values: F) => {
        const combinedValues = { ...defaultQueryParams, ...values };

        const newUrlState = queryParamsSchema?.parse(combinedValues);
        setUrlState(newUrlState ?? combinedValues);

        const newFilterValues = filterSchema.parse(combinedValues);
        setFilterValues(newFilterValues);

        onFilterSubmit?.(newFilterValues);

        if (toggleAfterSubmit || isSmallDevice) {
            toggleSidebar();
        }

        gainsightService.trackEvent('FilterSidebarApplyButtonClicked', {
            filter: newFilterValues,
            url: anonymizeUrl(window.location.href),
        });
    };

    const { renderFormContent, reset, watch, control } = useFormControls<F>({
        ...formProps,
        defaultValues: (defaultValuesParseResult.success
            ? defaultValuesParseResult.data
            : defaultFilterValues) as UseFormProps<F>['defaultValues'],
        id: formId,
        values: filterValues,
        onSubmit: handleSubmit,
        resolver: createZodFormResolver(filterSchema),
    });

    return (
        <Stack spacing={contentSpacing} fill tabletMax={{ spacing: showSidebar ? '0' : '300' }}>
            <Container overflowY="auto" width="100%">
                {typeof children === 'function'
                    ? children({ filterValues, filterSidebarOpened: showSidebar })
                    : children}
            </Container>
            <FilterSidebar
                toggle={toggleSidebar}
                show={showSidebar}
                hideOffset={contentSpacing}
                title={filterSidebar.title}
                primaryButton={{
                    label: submitButtonLabel ?? lt('ViewReport'),
                    type: 'submit',
                    formId,
                }}
                actions={
                    <>
                        {withClearFilters ? (
                            <Button onClick={() => reset(defaultFilterValues)} type="button" size="medium">
                                {lt('ClearFilters')}
                            </Button>
                        ) : null}
                        {typeof filterSidebar.actions === 'function'
                            ? filterSidebar.actions({ filterValues, watch, control })
                            : filterSidebar.actions}
                    </>
                }
                loading={loading}
            >
                {renderFormContent(filterSidebar.fields)}
            </FilterSidebar>
        </Stack>
    );
};
